import { isAfter, parseISO, subDays } from 'date-fns';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Calendar } from 'primereact/calendar';
import { Column, type ColumnEditorOptions } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
    type DataTableRowEditEvent,
} from 'primereact/datatable';
import React, { useEffect, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    H2Regular,
    useToastController,
    YStack,
} from 'smoothie';

import { GroveSimpleModal } from '../../../common/components/GroveSimpleModal';
import { formatToSimpleDateString } from '../../../common/utils/formatting';
import {
    adminUserAtom,
    mutatedAdminUserGuideAtom,
} from '../../../entities/entityAtoms';
import { type Guide } from '../../../entities/entityTypes';
import { TrackedEvent } from '../../../logger/Events';
import { trackEvent } from '../../../logger/Logger';

export const isOutOfOfficeModalOpenAtom = atom(false);

export const OutOfOfficeModal = () => {
    const [isOutOfOfficeModalOpen, setIsOutOfOfficeModalOpen] = useAtom(
        isOutOfOfficeModalOpenAtom,
    );
    const closeModal = () => {
        setIsOutOfOfficeModalOpen(false);
    };

    return (
        <GroveSimpleModal
            isOpen={isOutOfOfficeModalOpen}
            hasCloseButton={true}
            onClose={closeModal}
            renderInAppRoot={false}
        >
            <YStack
                marginHorizontal={16}
                marginTop={32}
                width="90vw"
                maxWidth={1000}
            >
                <H2Regular>Out-Of-Office</H2Regular>
                <BodySmallRegular marginVertical={16}>
                    During the OOO period, if a member messages you, they will
                    receive an auto-response (once per day until the end date).
                </BodySmallRegular>
                <OutOfOfficeWidget />
            </YStack>
        </GroveSimpleModal>
    );
};

const OutOfOfficeWidget = () => {
    const toast = useToastController();
    const adminUser = useAtomValue(adminUserAtom);
    const canUseGuideMessaging = adminUser.authorization.guideMessaging.canUse;

    const setMutatedAdminUserGuide = useSetAtom(mutatedAdminUserGuideAtom);

    const [editingRows, setEditingRows] = useState({});
    const onRowEditComplete = ({ newData }: DataTableRowEditCompleteEvent) => {
        const updatedAdminUserGuide = newData as Guide;
        const { oooStart, oooEnd } = updatedAdminUserGuide;

        // Validate
        let error: string | null = null;
        if (
            (oooStart != null && oooEnd == null) ||
            (oooStart == null && oooEnd != null)
        ) {
            error = 'Must select both a start and end date for out-of-office.';
        }

        if (
            oooStart != null &&
            oooEnd != null &&
            isAfter(parseISO(oooStart), parseISO(oooEnd))
        ) {
            error = 'Out-of-office start date must be before end date.';
        }

        const yesterday = subDays(new Date(), 1);
        if (oooEnd != null && !isAfter(parseISO(oooEnd), yesterday)) {
            error = 'Out-of-office end date must be in the future.';
        }

        if (error != null) {
            toast.show(error, { duration: 2000 });
            return;
        }

        setMutatedAdminUserGuide(updatedAdminUserGuide);
        trackEvent(TrackedEvent.GuideOutOfOfficeUpdated, {
            oooCleared: oooStart == null && oooEnd == null,
        });
    };
    const onRowEditChange = (event: DataTableRowEditEvent) => {
        setEditingRows(event.data);
    };

    useEffect(() => {
        const isInEditMode =
            adminUser.guide != null &&
            (adminUser.guide.oooStart == null ||
                adminUser.guide.oooEnd == null);

        if (!adminUser.guide) {
            return;
        }

        if (!isInEditMode) {
            return;
        }

        setEditingRows({ ...{ [adminUser.guide.id]: true } });
    }, []);

    if (!adminUser.guide) {
        return null;
    }

    return (
        <DataTable
            dataKey="id"
            value={[adminUser.guide]}
            size="small"
            tableStyle={{ width: '100%' }}
            editMode="row"
            editingRows={editingRows}
            onRowEditChange={onRowEditChange}
            onRowEditComplete={onRowEditComplete}
            showGridlines
        >
            <Column
                field="oooStart"
                header="First day out of office"
                editor={(options) => DateTextEditor(options)}
                body={StartDateRenderer}
            />
            <Column
                field="oooEnd"
                header="Last day out of office"
                editor={(options) => DateTextEditor(options)}
                body={EndDateRenderer}
            />
            <Column rowEditor hidden={!canUseGuideMessaging} />
        </DataTable>
    );
};

const StartDateRenderer = ({ oooStart }: Guide) =>
    oooStart === undefined ? null : (
        <BodyMediumRegular>
            {formatToSimpleDateString(oooStart, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
            })}
        </BodyMediumRegular>
    );

const EndDateRenderer = ({ oooEnd }: Guide) =>
    oooEnd === undefined ? null : (
        <BodyMediumRegular>
            {formatToSimpleDateString(oooEnd, {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
            })}
        </BodyMediumRegular>
    );

const DateTextEditor = (options: ColumnEditorOptions) => {
    const value = (options.value as string)
        ? new Date(options.value as string)
        : null;
    return (
        <Calendar
            value={value}
            onChange={(e) => {
                options.editorCallback?.(e.value?.toISOString() ?? null);
            }}
            inputStyle={{ width: '100%', fontSize: '0.85rem' }}
            placeholder="MM/DD/YYYY"
            minDate={new Date('2024-01-01')}
            showButtonBar
        />
    );
};
